import * as LPToken from '../contracts/lptoken'
import { setBalanceLPT } from '../modules/web3/actions'
import { useDispatch } from 'react-redux'
import { amountFromWei, amountToWei, bsToString } from '../utils/amount'
import { Big, BigSource } from 'big.js'
import useMultiStepTx from './useMultiStepTx'
import axios from 'axios'
import { NETWORK_VERSION } from './useWeb3'
import { getGasFeesPolygon } from '../api'
import { STAKED_DECIMALS } from '../page_components/Staking'
import { ethers } from 'ethers'

export interface ILPTokenContract {
  mint: (amount: BigSource, updateBalance: () => Promise<void>) => Promise<void>
  increaseAllowance: (spender: string, amount: BigSource) => Promise<void>
  approve: (address: string, amount: BigSource) => Promise<void>
  allowance: (account: string, spender: string) => Promise<string>
  balanceOf: () => Promise<void>
  getNameSymbol: (
    address: string,
    abi?: ethers.InterfaceAbi,
  ) => Promise<{ tokenName: string; tokenSymbol: string }>
  getDecimals: (address: string) => Promise<void>
  getCurrentEpochAmount: () => Promise<string>
  getTotalEpochAmount: () => Promise<string>
  getEndOfBuyCycle: () => Promise<any>
  getSuperchargeRewardsInEpoch: () => Promise<any>
  transfer: (address: string, amount: number) => Promise<boolean>
}

const initToken = (
  provider: ethers.BrowserProvider | ethers.JsonRpcProvider,
  account?: string,
) => {
  if (provider) {
    return new ethers.Contract(
      LPToken.address,
      LPToken.abi as ethers.InterfaceAbi,
      provider,
    )
  }
}

const useLPTokenContract = (
  provider: ethers.BrowserProvider | ethers.JsonRpcProvider,
  account?: string,
) => {
  const contract = initToken(provider, account)
  const dispatch = useDispatch()

  const { nextStepCallback } = useMultiStepTx()

  async function mint(
    amount: BigSource,
    updateBalance: (account: string) => Promise<void>,
  ) {
    await contract?.mint(account, bsToString(amount))
    await updateBalance(account)
  }

  async function approve(address: string, amount: BigSource) {
    let maxFeePerGas = new Big(400000000000) // fallback to 40 gwei
    let maxPriorityFeePerGas = new Big(400000000000) // fallback to 40 gwei
    try {
      const { data } = await axios({
        method: 'get',
        url:
          NETWORK_VERSION !== 137
            ? 'https://gasstation-mainnet.matic.network/v2'
            : 'https://gasstation-mumbai.matic.today/v2',
      })

      // maxFeePerGas = new Big(
      //   Math.ceil(data.fast.maxFee) + '',
      //   'gwei'
      // )
      // maxPriorityFeePerGas = new(
      //   Math.ceil(data.fast.maxPriorityFee) + '',
      //   'gwei'
      // )
      const gas = await contract?.approve.estimateGas(
        address,
        bsToString(amount),
      )

      return await contract?.approve(address, bsToString(amount), {
        gas,
        maxFeePerGas,
        maxPriorityFeePerGas,
      })
    } catch {
      console.error('failed to get gas estimation ')
    }
  }

  async function increaseAllowance(spender: string, amount: BigSource) {
    const { maxFeePerGas, maxPriorityFeePerGas } = await getGasFeesPolygon()

    const gas = await contract?.approve.estimateGas(spender, bsToString(amount))

    return await contract?.approve(spender, bsToString(amount), {
      gas,
      maxFeePerGas,
      maxPriorityFeePerGas,
    })
  }

  async function balanceOf() {
    if (account) {
      const fromChainBalance = await contract
        ?.balanceOf(account)
        .catch((err: any) => console.error(err, 'lp error'))

      if (fromChainBalance) {
        const balance = new Big(fromChainBalance)
        dispatch(
          setBalanceLPT(
            amountFromWei(new Big(balance ?? '0'), STAKED_DECIMALS).toString(),
          ),
        )
      } else {
        dispatch(setBalanceLPT('0'))
      }
    }
  }

  const allowance = async (account: string, spender: string) =>
    await contract?.allowance(account, spender)

  async function transfer(recipient: string, amount: number): Promise<boolean> {
    const toSend = amountToWei(new Big(amount), STAKED_DECIMALS)

    const isExcluded = await contract?.isExcludedFromFee(recipient)

    const gas = await contract?.transfer.estimateGas(
      recipient,
      parseInt(toSend.toString()),
    )

    return await contract?.transfer(recipient, parseInt(toSend.toString()), {
      gasLimit: gas,
    })
  }

  async function getNameSymbol(address: string, abi?: ethers.InterfaceAbi) {
    const nameSymbol = new ethers.Contract(
      address,
      abi || (LPToken.abi as ethers.InterfaceAbi),
    )
    const tokenName = await nameSymbol?.name()
    const tokenSymbol = await nameSymbol?.symbol()
    return { tokenName, tokenSymbol }
  }

  async function getDecimals(address: string) {
    const decimals = new ethers.Contract(
      address,
      LPToken.abi as ethers.InterfaceAbi,
    )
    return await decimals?.decimals()
  }

  const getCurrentEpochAmount = async (): Promise<any> => {
    return await contract?.epochCurrentAmt()
  }

  const getTotalEpochAmount = async (): Promise<any> => {
    return await contract?.epochEndAmt()
  }

  const getEndOfBuyCycle = async (): Promise<any> => {
    return await contract?.buySellTime()
  }

  const getSuperchargeRewardsInEpoch = async (): Promise<any> => {
    return await contract?.superChgReward()
  }

  return {
    mint,
    increaseAllowance,
    balanceOf,
    getNameSymbol,
    getDecimals,
    approve,
    transfer,
    allowance,
    getCurrentEpochAmount,
    getTotalEpochAmount,
    getEndOfBuyCycle,
    getSuperchargeRewardsInEpoch,
  }
}

export default useLPTokenContract
